<template>
  <v-container fluid class="mt-16 mb-3 py-6">
    <v-row>
      <v-spacer />
      <v-col cols="12" md="6">
        <h1
          class="monkey-title text-h4 mb-6 text-accent"
          :class="!lgAndUp ? 'text-center' : ''"
        >Votre panier</h1>

        <p
          v-if="cartItems.length === 0"
          class="text-medium-emphasis"
          :class="!lgAndUp ? 'text-center' : ''"
        >Votre panier semble vide...</p>
        <template v-else>
          <InlineProduct
            v-for="productData in cartItems"
            :key="productData.id"
            :product="productData"
            @remove-product="removeProduct"
            @set-quantity="setProduct"
          />
        </template>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="neu-card">
          <v-card-text>
            <v-row class="align-start my-0">
              <v-col>
                <h2 class="text-subtitle-1">Sous-total</h2>
              </v-col>
              <v-col cols="auto">
                <h2 class="text-overline">{{ Number(subTotal).toLocaleString(undefined, {minimumFractionDigits: 2}) }} €</h2>
              </v-col>
            </v-row>

            <v-row class="align-start my-0">
              <v-col>
                <h2 class="text-subtitle-1">Frais de livraison</h2>
                <p class="text-caption text-medium-emphasis">Livraison express Colissimmo</p>
              </v-col>
              <v-col cols="auto">
                <h2 class="text-overline">{{ Number(deliveryFees).toLocaleString(undefined, {minimumFractionDigits: 2}) }} €</h2>
              </v-col>
            </v-row>

            <v-divider
              color="primary"
              class="my-4 border-opacity-100"
            />

            <v-row class="align-start my-0">
              <v-col>
                <h2 class="text-subtitle-1">Total</h2>
              </v-col>
              <v-col cols="auto">
                <h2 class="text-overline">{{ Number(total).toLocaleString(undefined, {minimumFractionDigits: 2}) }} €</h2>
              </v-col>
            </v-row>

            <div class="mt-6">
              <v-btn
                color="primary"
                class="neu-shadow"
                block
                :loading="generatingCheckoutLink"
                :disabled="cartItems.length === 0"
                @click="generateCheckoutLink()"
              >Valider mon panier</v-btn>
              <p v-if="checkoutLinkError" class="text-error text-center mt-3">{{ checkoutLinkError }}</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-spacer />
    </v-row>
  </v-container>
</template>

<script setup>
import { useDisplay } from "vuetify";

const { lgAndUp } = useDisplay();

definePageMeta({
  title: "Panier",
});
</script>

<script>
import axios from "axios";

import InlineProduct from "@/components/InlineProduct";
import { useStoreStore } from "~/stores/store";
import { useCartStore } from "~/stores/cart";

export default {
  name: "CartPage",
  components: {
    InlineProduct,
  },
  data() {
    return {
      cartItems: [],

      subTotal: 0,
      deliveryFees: 0,

      generatingCheckoutLink: false,
      checkoutLinkError: null,
    };
  },
  computed: {
    total: function() {
      return this.subTotal + this.deliveryFees;
    },
  },
  watch: {
    cartItems: {
      handler() {
        this.subTotal = 0;

        this.cartItems.forEach((product) => {
          if (!product.fullValue) return;

          this.subTotal += product.fullValue;
        });
      },
      deep: true,
    },
  },
  methods: {
    findWithAttr(array, attr, value) {
      for (let i = 0; i < array.length; i += 1)
        if (array[i][attr] === value)
          return i;

      return -1;
    },
    removeProduct(productId) {
      const cartStore = useCartStore();

      const productCartIndex = this.findWithAttr(this.cartItems, "id", productId);

      this.cartItems.splice(productCartIndex, 1);

      cartStore.removeItem(productId);
    },
    setProduct(productId, quantity) {
      const cartStore = useCartStore();

      const productCartIndex = this.findWithAttr(this.cartItems, "id", productId);

      if (quantity === 0) this.removeProduct(productId);
      else {
        cartStore.setItem(productId, quantity - this.cartItems[productCartIndex].quantity);

        this.cartItems[productCartIndex] = {
          ...this.cartItems[productCartIndex],
          quantity,
          fullValue: this.cartItems[productCartIndex].value * quantity,
        };
      }
    },

    generateCheckoutLink() {
      const config = useRuntimeConfig();

      this.generatingCheckoutLink = true;
      this.checkoutLinkError = null;

      let productsList = "";

      this.cartItems.forEach((item) => {
        if (!item.integrations?.stripe?.productId) return;

        const stripeProductId = item.integrations?.stripe?.productId;

        productsList += `${stripeProductId}|${item.quantity},`;
      });

      axios({
        method: "post",
        url: `${config.public.apiBase}/createCheckoutSession`,
        headers: {
          Authorization: `Bearer ${config.public.apiToken}`,
        },
        data: {
          productsList: productsList.replace(/,$/gm, ""),
        },
      }).then((response) => {
        const checkoutLinkData = response.data;

        const cartStore = useCartStore();
        cartStore.emptyCart();
        this.cartItems = [];

        window.location.href = checkoutLinkData.checkout.url;
      }).catch((error) => {
        this.checkoutLinkError = "Une erreur est survenue lors de la création de votre lien de paiement, merci de réessayer ultérieurement.";

        console.error(error);
      }).finally(() => {
        this.generatingCheckoutLink = false;
      });
    },
  },
  async created() {
    const cartStore = useCartStore();
    const storeStore = useStoreStore();

    if (cartStore.allItemsSize === 0) return;

    this.cartItems = [];

    for (const key in cartStore.cartItems) {
      const productData = await storeStore.fetchProduct(key);

      const price = productData.value * cartStore.cartItems[key];

      this.cartItems.push({
        id: key,
        quantity: cartStore.cartItems[key],
        fullValue: price,
        ...productData,
      });

      this.subTotal += price;
    }
  },
};
</script>
